import type { ReactElement } from "react";
import { Download, Eye, EyeOff, FolderPlus, Trash2, X } from "lucide-react";
import { Button } from "../../../../shared/components/ui/button";

interface BulkActionsToolbarProps {
  readonly selectedCount: number;
  readonly onClearSelection: () => void;
  readonly onPublish: () => void;
  readonly onUnpublish: () => void;
  readonly onDelete: () => void;
  readonly onAddToCollection: () => void;
  readonly onExportCsv: () => void;
}

/**
 * Toolbar that appears when titles are selected
 * Provides bulk action buttons
 */
export function BulkActionsToolbar({
  selectedCount,
  onClearSelection,
  onPublish,
  onUnpublish,
  onDelete,
  onAddToCollection,
  onExportCsv,
}: BulkActionsToolbarProps): ReactElement {
  return (
    <div className="fixed bottom-16 left-1/2 -translate-x-1/2 z-50">
      <div className="bg-neutral-900/50 border border-neutral-800 backdrop-blur-md rounded-full shadow-2xl p-4 flex items-center gap-4">
        {/* Selection Count */}
        <div className="flex items-center gap-2 px-3 py-1 text-lumi-accent rounded font-base text-sm">
          <span>{selectedCount} selected</span>
          <button
            type="button"
            onClick={onClearSelection}
            className="hover:opacity-70 transition-opacity"
            aria-label="Clear selection"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Divider */}
        <div className="h-8 w-px bg-neutral-700" />

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <Button
            onClick={onPublish}
            variant="ghost-sm"
            size="sm"
            className="text-xs text-green-400 hover:text-green-300 rounded-full hover:bg-green-900/20"
          >
            <Eye className="h-4 w-4 mr-2" />
            Publish
          </Button>

          <Button
            onClick={onUnpublish}
            variant="ghost-sm"
            size="sm"
            className="text-xs text-neutral-400 hover:text-neutral-300 rounded-full hover:bg-neutral-800"
          >
            <EyeOff className="h-4 w-4 mr-2" />
            Unpublish
          </Button>

          <Button
            onClick={onAddToCollection}
            variant="ghost-sm"
            size="sm"
            className="text-xs text-blue-400 hover:text-blue-300 rounded-full hover:bg-blue-900/20"
          >
            <FolderPlus className="h-4 w-4 mr-2" />
            Add to Collection
          </Button>

          <Button
            onClick={onExportCsv}
            variant="ghost-sm"
            size="sm"
            className="text-xs text-lumi-accent hover:text-lumi-accent/80 rounded-full hover:bg-lumi-accent/10"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>

          <div className="h-8 w-px bg-neutral-700" />

          <Button
            onClick={onDelete}
            variant="ghost-sm"
            size="sm"
            className="text-xs text-red-400 hover:text-red-300 rounded-full hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}
